/**
 * 类型趋势图表渲染子组件
 * 负责折线图和柱状图的渲染，支持维度名称翻译
 */
import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_V, DBO_EC } from '@op/utils/format';
import { chartAllColors } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import type { ITypeTrendEChartProps } from './types';

const TypeTrendEChart: React.FC<ITypeTrendEChartProps> = ({
  chartData,
  chartType,
  dimensionMap,
  height = 280,
  filter
}) => {
  // ==================== 多语言 ====================
  const { isEn, formatMessage } = useLanguage();

  // ==================== 维度名称翻译 ====================
  const getDimensionName = (key: string) => {
    const dimension = dimensionMap[key];
    if (!dimension) return key;
    return isEn ? dimension.en : dimension.zh;
  };

  // ==================== 图表配置 ====================
  const chartOption = useMemo(() => {
    if (!chartData?.series?.length || chartData?.isEmpty) return null;

    const xAxisData = chartData.xAxis || [];

    return {
      color: chartAllColors,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: chartType === 'bar' ? 'shadow' : 'line' },
        formatter: (params: any) =>
          formatChartParamsDate({
            title: DBO_EC.toolTime(params[0]?.name, filter?.date_type || 'daily') || '',
            params,
            isSort: true,
            valueFormat: (value: number) => DBO_V.reviews(value),
            listTitleFunc: (item: any) => getDimensionName(item.seriesName),
            dealwithRecords(records, total) {
              records.unshift({
                text: formatMessage('Sum'),
                value: `${DBO_V.reviews(total)}`,
                originValue: total,
                originText: '',
              });
              return records;
            },
          }),
      },
      grid: { top: 20 },
      legend: {
        bottom: 0,
        data: chartData.legend?.map((item: string) => getDimensionName(item)),
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          formatter: (value: string) => DBO_EC.timeX(value, filter?.date_type || 'daily'),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: DBO_EC.reviews,
        },
      },
      series: chartData.series.map((item: any) => ({
        name: getDimensionName(item.name),
        type: chartType === 'bar' ? 'bar' : 'line',
        data: item.data.map((v: number) => v || undefined),
        stack: chartType === 'bar' ? 'total' : undefined,
        emphasis: {
          focus: 'series',
        },
      })),
    };
  }, [chartData, chartType, dimensionMap, isEn, filter, getDimensionName]);

  // ==================== 渲染逻辑 ====================
  return (
    <div className="dbo-type-trend-echart">
      <EChartCommon
        options={chartOption}
        height={height}
        empty={!chartData?.series?.length || chartData?.isEmpty}
      />
    </div>
  );
};

export default React.memo(TypeTrendEChart);
